import type { View, WidgetConfig } from "../lib/config";

type ToolbarProps = {
  config: WidgetConfig;
  view: View;
  onViewChange: (view: View) => void;
  anchorDate: Date;
  onAnchorDateChange: (date: Date) => void;
  query: string;
  onQueryChange: (query: string) => void;
};

const SWITCHABLE_VIEWS: { id: View; label: string }[] = [
  { id: "pinboard", label: "Pinboard" },
  { id: "agenda", label: "Agenda" },
  { id: "monthly", label: "Monthly" },
];

function toLocalDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function Toolbar({
  config,
  view,
  onViewChange,
  anchorDate,
  onAnchorDateChange,
  query,
  onQueryChange,
}: ToolbarProps) {
  const stepDate = (delta: number) => {
    // Snap to day 1 to avoid month-overflow (e.g. Jan 31 + 1mo → Mar 3).
    // The date nav only renders for agenda/monthly/pinboard (list is gated
    // out below), and all three now step by month.
    onAnchorDateChange(
      new Date(anchorDate.getFullYear(), anchorDate.getMonth() + delta, 1),
    );
  };

  const handleDateInput = (value: string) => {
    if (!value) return;
    const parts = value.split("-").map(Number);
    if (parts.length !== 3) return;
    const [y, m, d] = parts;
    if (!y || !m || !d) return;
    onAnchorDateChange(new Date(y, m - 1, d));
  };

  return (
    <div className="gitg-toolbar">
      {config.showSearch ? (
        <div className="gitg-toolbar-search">
          <input
            type="search"
            className="gitg-toolbar-search-input"
            placeholder="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            aria-label="Search events"
          />
          {query ? (
            <button
              type="button"
              className="gitg-toolbar-search-clear"
              onClick={() => onQueryChange("")}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>
      ) : null}

      {config.showDateNav && view !== "list" ? (
        <div className="gitg-toolbar-date-nav">
          <button
            type="button"
            className="gitg-toolbar-date-btn"
            onClick={() => stepDate(-1)}
            aria-label="Previous month"
          >
            ‹
          </button>
          <input
            type="date"
            className="gitg-toolbar-date-input"
            value={toLocalDateInputValue(anchorDate)}
            onChange={(e) => handleDateInput(e.target.value)}
            aria-label="Date"
          />
          <button
            type="button"
            className="gitg-toolbar-date-btn"
            onClick={() => stepDate(1)}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      ) : null}

      <div className="gitg-toolbar-spacer" />

      {config.showViewSwitcher ? (
        <div className="gitg-toolbar-views" role="tablist">
          {SWITCHABLE_VIEWS.map((v) => {
            const isActive = view === v.id;
            return (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={
                  isActive
                    ? "gitg-toolbar-view-btn gitg-toolbar-view-btn--active"
                    : "gitg-toolbar-view-btn"
                }
                onClick={() => onViewChange(v.id)}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
